import { create } from "zustand";
import {
  DRILLDOWN_GROUP_BY_FEATURES_COMPETITORS,
  DRILLDOWN_GROUP_BY_FEATURES_MY_BRAND,
  DRILLDOWN_SEGMENT_NONE,
  hasCrossLevelOr,
  remapAudienceSegmentValues,
  toDrilldownDimensionField,
  type AdvancedFilters,
  type FilterConnector,
  type FilterGroup,
  type FilterRule,
} from "@nexus/contracts";
import { DEFAULT_MY_BRAND_KPIS } from "../lib/my-brand-kpi-catalog";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
// The effective-status vocabulary is shared with the Overview filter bar, so it lives in
// `@/lib/ad-status`. Re-exported below under the historical Drilldown names.
import {
  COMPETITOR_EFFECTIVE_STATUS_OPTIONS as EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_INACTIVE_STATUSES,
} from "@/lib/ad-status";

export type { AdvancedFilters, FilterConnector, FilterGroup, FilterRule };
export { EFFECTIVE_STATUS_OPTIONS, MY_BRAND_EFFECTIVE_STATUS_OPTIONS, MY_BRAND_INACTIVE_STATUSES };

/** Which mode's draft advanced-filters field the CRUD actions below should mutate —
 *  imperative read (not a subscription), safe outside React render. Advanced filters
 *  have a different field vocabulary per mode (Groupby fields plus Days/Assets for
 *  Competitors vs Groupby fields plus KPI ids for My Brand), so — like Ad Type/Status
 *  and sort — each mode keeps its own draft/applied pair; only the mutation actions
 *  need to resolve which one is "active" right now, since the toolbar shows exactly
 *  one list at a time. */
function isMyBrandMode(): boolean {
  return useDashboardFiltersStore.getState().mode === "my-brand";
}

// ─── View mode ────────────────────────────────────────────────────────────────

export type DrilldownViewMode = "grid" | "list" | "chart";

// ─── Sort ─────────────────────────────────────────────────────────────────────
//
// Competitors and My Brand each keep their OWN last-used sort (namespaced fields)
// rather than sharing one — switching modes doesn't clobber either mode's choice.
// `DrilldownPage.tsx` collapses whichever pair is active into the single
// `sortMode`/`sortDirection` value the API actually receives.

/** Competitors-only sort field (fixed aggregate labels). */
export type DrilldownSortMode = "Days" | "Assets" | "Days/Assets" | "Date";
export type DrilldownSortDirection = "asc" | "desc";

export const SORT_MODE_OPTIONS: readonly { label: string; value: DrilldownSortMode }[] = [
  { label: "Days / Assets", value: "Days/Assets" },
  { label: "Days", value: "Days" },
  { label: "Assets", value: "Assets" },
  { label: "Date", value: "Date" },
] as const;

/** My Brand's minimum KPI pill count — the X button disables rather than dropping below this. */
export const MIN_MY_BRAND_KPIS = 1;

// ─── Option constants ───────────────────────────────────────────────────────

/**
 * Fallback labels for segment values (`'Hash'` → `'Creative'`). The Group By
 * dropdown itself is data-driven from `features_mapping`
 * (`GET /drilldown/group-by-options`). This list feeds `segmentLabel` and the
 * `isError`-only `fallbackSegmentOptions` helper.
 *
 * `value` = Feature column (the BQ CASE label, must match exactly);
 * `label` = Display_Name column (what the user sees in the UI).
 * "Similar *" variants are intentionally excluded — they expose internal cluster IDs.
 */
export interface SegmentOption {
  label: string;
  value: string;
}

export const SEGMENT_OPTIONS: readonly SegmentOption[] = [
  { label: "Creative", value: "Hash" },
  { label: "Text Overlay (Hook)", value: "Exact Text Overlay (Hook)" },
  { label: "Voice Over (Hook)", value: "Exact Voice Over (Hook)" },
  { label: "Text Overlay (Body)", value: "Exact Text Overlay (Post-Hook)" },
  { label: "Voice Over (Body)", value: "Exact Voice Over (Post-Hook)" },
  { label: "Visual (Hook)", value: "Exact Visual (Hook)" },
  { label: "Visual (Body)", value: "Exact Visual (Post-Hook)" },
  { label: "Full Text Overlay", value: "Full Text Overlay" },
  { label: "Full Voice Over", value: "Full Voice Over" },
  { label: "Full Visual", value: "Full Visual" },
  { label: "Client", value: "Client" },
  { label: "Account Name", value: "Account Name" },
  { label: "Ad", value: "Ad ID" },
  { label: "Ad Name", value: "Ad Name" },
  { label: "Adset ID", value: "Adset ID" },
  { label: "Adset Name", value: "Adset Name" },
  { label: "Campaign Name", value: "Campaign Name" },
  { label: "Campaign ID", value: "Campaign ID" },
  { label: "Headline", value: "Headline" },
  { label: "Primary", value: "Primary" },
  { label: "Start Date", value: "Start Date" },
  { label: "Link", value: "Link" },
  { label: "Hook", value: "Hook" },
  { label: "Body", value: "Body" },
  { label: "Industry", value: "Industry" },
  { label: "Competitor", value: "Competitor" },
] as const;

/**
 * Mapping Type='Drilldown' Feature values by Platform — same sets as
 * `DRILLDOWN_GROUP_BY_FEATURES_*` in `@nexus/contracts`. Used when
 * `GET /drilldown/group-by-options` errors so Competitors never gains core-only
 * dimensions (Client, Account Name, campaign/adset).
 */
export function fallbackSegmentOptions(mode: "my-brand" | "competitors"): SegmentOption[] {
  const allowed = new Set<string>(
    mode === "my-brand"
      ? DRILLDOWN_GROUP_BY_FEATURES_MY_BRAND
      : DRILLDOWN_GROUP_BY_FEATURES_COMPETITORS,
  );
  return SEGMENT_OPTIONS.filter((option) => allowed.has(option.value));
}

/** Competitors-only — My Brand has its own, smaller ad-format set (see below). */
export const OBJECT_TYPE_OPTIONS: readonly SegmentOption[] = [
  { label: "Video", value: "Video" },
  { label: "Image", value: "Image" },
  { label: "Carousel", value: "Carousel" },
] as const;

/** My Brand only offers Image/Video ad formats (no Carousel). */
export const MY_BRAND_OBJECT_TYPE_OPTIONS: readonly SegmentOption[] = [
  { label: "Video", value: "Video" },
  { label: "Image", value: "Image" },
] as const;

/** UI label for a segment value ('Hash' → 'Creative'). */
export function segmentLabel(value: string): string {
  return SEGMENT_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

/** Sentinel meaning "this segment slot is inactive". On slot 1 it is "No grouping". */
export const SEGMENT_NONE = DRILLDOWN_SEGMENT_NONE;
export const DEFAULT_SEGMENT_1 = "Hash";
/** Field seeded into a new advanced-filter rule — "Hash" is a dimension in both modes. */
export const DEFAULT_FILTER_FIELD = toDrilldownDimensionField(DEFAULT_SEGMENT_1);

const LEGACY_GROUPBY_FIELDS = ["Groupby1", "Groupby2", "Groupby3"] as const;

/**
 * Rewrite legacy `GroupbyN` rules (saved presets, old URLs) to the equivalent
 * `dim:<segmentN>` field so they stay editable now that the field picker only offers
 * dimension fields. Filtering the raw rows on a grouped dimension returns the same rows
 * as filtering its group key. A rule on an empty slot has no dimension and is kept as-is.
 */
export function normalizeLegacyGroupbyRules(
  groups: AdvancedFilters,
  segments: readonly [string, string, string],
): AdvancedFilters {
  return groups.map((group) => ({
    ...group,
    rules: group.rules.map((rule) => {
      const slot = LEGACY_GROUPBY_FIELDS.indexOf(rule.field as (typeof LEGACY_GROUPBY_FIELDS)[number]);
      const segment = segments[slot];
      if (segment === undefined || segment === SEGMENT_NONE) return rule;
      return { ...rule, field: toDrilldownDimensionField(segment) };
    }),
  }));
}

// ─── Store ────────────────────────────────────────────────────────────────────

export interface DrilldownFiltersState {
  // --- View mode (display preference, not a query filter) ---
  viewMode: DrilldownViewMode;
  setViewMode: (mode: DrilldownViewMode) => void;
  // --- Sort (applied immediately — no draft/applied split). Namespaced per mode so
  // switching Competitors <-> My Brand doesn't clobber either mode's last sort. ---
  competitorSortMode: DrilldownSortMode;
  competitorSortDirection: DrilldownSortDirection;
  setCompetitorSortMode: (mode: DrilldownSortMode) => void;
  setCompetitorSortDirection: (direction: DrilldownSortDirection) => void;
  /** Ordered My Brand KPI pill selection — pill order == table column order. */
  myBrandKpis: string[];
  /** Active My Brand sort field id (one of MY_BRAND_KPI_CATALOG's ids) + direction. */
  myBrandSortMode: string;
  myBrandSortDirection: DrilldownSortDirection;
  /** Drag-reorder: replace the full ordered id list (e.g. via dnd-kit's arrayMove). */
  setMyBrandKpis: (ids: string[]) => void;
  /** No-op if `id` is already selected. */
  addMyBrandKpi: (id: string) => void;
  /** No-op if removing would drop the selection below MIN_MY_BRAND_KPIS. Removing the
   *  active sort's KPI moves the sort to the next remaining pill (same direction).
   *  `visibleIds`, when passed, is the resolved pill list: the stored selection is
   *  replaced with what remains of it, and the sort fallback is chosen from that
   *  list so a hidden or unknown id cannot become the sort. */
  removeMyBrandKpi: (id: string, visibleIds?: string[]) => void;
  /** Swap `oldId` for `newId` in the same slot (position preserved). */
  replaceMyBrandKpi: (oldId: string, newId: string) => void;
  /** Click a pill's sort arrow: makes `id` the active sort (desc) if it wasn't, else
   *  flips the current direction. Only one KPI can be the active sort at a time. */
  toggleMyBrandKpiSort: (id: string) => void;
  /** My Brand list-view date columns the user hid (`min_date`, `max_date`, `days`). */
  myBrandHiddenColumns: string[];
  /** Hide a visible column, or show it again if it is already hidden. */
  toggleMyBrandColumn: (key: string) => void;
  // --- Draft (toolbar edits these; not yet applied to query) ---
  objectType: string[];
  effectiveStatus: string[];
  startDate: string; // 'YYYY-MM-DD' or '' (open bound)
  endDate: string;
  segment1: string;
  segment2: string;
  segment3: string;
  // --- Applied (drives the query; updated only via applyFilters / reset) ---
  appliedObjectType: string[];
  appliedEffectiveStatus: string[];
  appliedStartDate: string;
  appliedEndDate: string;
  appliedSegment1: string;
  appliedSegment2: string;
  appliedSegment3: string;

  // --- My Brand's own Ad Type/Status selections (namespaced — different value domain
  // than Competitors', same draft/applied split; see MY_BRAND_OBJECT_TYPE_OPTIONS /
  // MY_BRAND_EFFECTIVE_STATUS_OPTIONS above). ---
  myBrandObjectType: string[];
  myBrandEffectiveStatus: string[];
  appliedMyBrandObjectType: string[];
  appliedMyBrandEffectiveStatus: string[];

  // --- My Brand's own date range (namespaced — same draft/applied split as above; each
  // mode remembers its own picked range, so switching modes doesn't leak one mode's
  // dates onto the other's query). ---
  myBrandStartDate: string;
  myBrandEndDate: string;
  appliedMyBrandStartDate: string;
  appliedMyBrandEndDate: string;

  // --- My Brand's own Group By segment selections (same SEGMENT_OPTIONS vocabulary as
  // Competitors', but namespaced so each mode remembers its own selection — same
  // reasoning as Ad Type/Status and advanced filters above). ---
  myBrandSegment1: string;
  myBrandSegment2: string;
  myBrandSegment3: string;
  appliedMyBrandSegment1: string;
  appliedMyBrandSegment2: string;
  appliedMyBrandSegment3: string;

  // --- Advanced filters (same draft/applied split). Namespaced per mode — the field
  // vocabulary differs (Groupby fields + Days/Assets for Competitors vs Groupby fields
  // + KPI ids for My Brand) — so switching modes must not carry one mode's filter rules
  // over as if they applied to the other's fields. `advancedFilters`/`appliedAdvancedFilters`
  // are Competitors' pair (unchanged field names, back-compat with existing URLs). ---
  advancedFilters: AdvancedFilters;
  appliedAdvancedFilters: AdvancedFilters;
  myBrandAdvancedFilters: AdvancedFilters;
  appliedMyBrandAdvancedFilters: AdvancedFilters;

  // --- My Brand Prospecting vs Retargeting (Core only — Competitors has no equivalent). ---
  audienceSegment: string[];
  appliedAudienceSegment: string[];
  device: string[];
  appliedDevice: string[];
  aspectRatio: string[];
  appliedAspectRatio: string[];

  setObjectType: (values: string[]) => void;
  setEffectiveStatus: (values: string[]) => void;
  setMyBrandObjectType: (values: string[]) => void;
  setMyBrandEffectiveStatus: (values: string[]) => void;
  setAudienceSegment: (values: string[]) => void;
  setDevice: (values: string[]) => void;
  /** Drop a device selection from draft and applied without committing other drafts. */
  clearDevice: () => void;
  setAspectRatio: (values: string[]) => void;
  /** Drop an aspect-ratio selection from draft and applied without committing other drafts. */
  clearAspectRatio: () => void;
  setDateRange: (startDate: string, endDate: string) => void;
  /** Set a specific segment slot (1-based). "No grouping" on slot 1 also clears slots 2–3. */
  setSegment: (slot: 1 | 2 | 3, value: string) => void;
  /** Add the next empty segment slot (slot 1 first, when "No grouping"), seeded with a value. */
  addSegment: (value: string) => void;
  /** Reset a segment slot back to 'All' (removing the pill). */
  removeSegment: (slot: 1 | 2 | 3) => void;
  /** Add a new filter group (with one default rule) to the draft advanced filters. */
  addFilterGroup: (defaultField?: string) => void;
  /** Remove a filter group by index. */
  removeFilterGroup: (groupIndex: number) => void;
  /** Toggle the connector for a group (And ↔ Or). */
  setGroupConnector: (groupIndex: number, connector: FilterConnector) => void;
  /** Append a default rule to a group. */
  addFilterRule: (groupIndex: number, defaultField?: string) => void;
  /** Remove a rule from a group; removes the group too if it becomes empty. */
  removeFilterRule: (groupIndex: number, ruleIndex: number) => void;
  /** Patch a rule within a group. */
  updateFilterRule: (groupIndex: number, ruleIndex: number, patch: Partial<FilterRule>) => void;
  /** Sync both modes' draft/applied/base dates from their own global date-range setting
   *  in one commit (no dirty state) — unlike setDateRange, this seeds BOTH modes at once
   *  since it runs on mount/URL-change rather than as a user edit to the active mode. */
  initializeDateRange: (
    startDate: string,
    endDate: string,
    myBrandStartDate: string,
    myBrandEndDate: string,
  ) => void;
  /** Update only the active mode's base date range without touching draft or applied
   *  (used when URL has explicit dates). */
  setBaseDateRange: (startDate: string, endDate: string) => void;
  // --- Base date range (last value set by initializeDateRange — used by reset).
  // Namespaced per mode too, since each mode's reset must restore its own base. ---
  baseStartDate: string;
  baseEndDate: string;
  myBrandBaseStartDate: string;
  myBrandBaseEndDate: string;
  /** Commit draft → applied (fires query). */
  applyFilters: () => void;
  /** Revert draft back to last applied snapshot. */
  discardFilters: () => void;
  /** Reset all page filters to defaults (both draft and applied). */
  reset: () => void;
  /** Apply a saved preset's page slice for one mode (draft + applied). Advanced filters with
   *  a cross-level Or stay draft-only, since the API rejects them; the editor flags them. */
  loadPresetPageState: (
    presetMode: 'my-brand' | 'competitors',
    page:
      | {
          myBrandObjectType: string[];
          myBrandEffectiveStatus: string[];
          audienceSegment: string[];
          device?: string[];
          aspectRatio?: string[];
          myBrandSegment1: string;
          myBrandSegment2: string;
          myBrandSegment3: string;
          myBrandAdvancedFilters: AdvancedFilters;
          myBrandKpis: string[];
          myBrandSortMode: string;
          myBrandSortDirection: DrilldownSortDirection;
          myBrandHiddenColumns?: string[];
          viewMode: DrilldownViewMode;
        }
      | {
          objectType: string[];
          effectiveStatus: string[];
          segment1: string;
          segment2: string;
          segment3: string;
          advancedFilters: AdvancedFilters;
          competitorSortMode: DrilldownSortMode;
          competitorSortDirection: DrilldownSortDirection;
          viewMode: DrilldownViewMode;
        },
  ) => void;
  /** Hydrate both draft and applied state from URL params (used by useDrilldownSearchSync). */
  hydrateFromApplied: (payload: {
    objectType: string[];
    effectiveStatus: string[];
    myBrandObjectType: string[];
    myBrandEffectiveStatus: string[];
    startDate: string;
    endDate: string;
    myBrandStartDate: string;
    myBrandEndDate: string;
    segment1: string;
    segment2: string;
    segment3: string;
    myBrandSegment1: string;
    myBrandSegment2: string;
    myBrandSegment3: string;
    viewMode: DrilldownViewMode;
    competitorSortMode: DrilldownSortMode;
    competitorSortDirection: DrilldownSortDirection;
    myBrandKpis: string[];
    myBrandSortMode: string;
    myBrandSortDirection: DrilldownSortDirection;
    myBrandHiddenColumns: string[];
    advancedFilters: AdvancedFilters;
    myBrandAdvancedFilters: AdvancedFilters;
    audienceSegment: string[];
    device: string[];
    aspectRatio: string[];
  }) => void;
}

const ALL_OBJECT_TYPES = OBJECT_TYPE_OPTIONS.map((o) => o.value);
const ALL_EFFECTIVE_STATUSES = EFFECTIVE_STATUS_OPTIONS.map((o) => o.value);
const ALL_MY_BRAND_OBJECT_TYPES = MY_BRAND_OBJECT_TYPE_OPTIONS.map((o) => o.value);
const ALL_MY_BRAND_EFFECTIVE_STATUSES = MY_BRAND_EFFECTIVE_STATUS_OPTIONS.map((o) => o.value);

/** Competitors-only fields — reset() must not touch these while in My Brand mode. */
const COMPETITORS_DEFAULTS = {
  objectType: ALL_OBJECT_TYPES,
  effectiveStatus: ALL_EFFECTIVE_STATUSES,
  appliedObjectType: ALL_OBJECT_TYPES,
  appliedEffectiveStatus: ALL_EFFECTIVE_STATUSES,
  startDate: "",
  endDate: "",
  appliedStartDate: "",
  appliedEndDate: "",
  segment1: DEFAULT_SEGMENT_1,
  segment2: SEGMENT_NONE,
  segment3: SEGMENT_NONE,
  appliedSegment1: DEFAULT_SEGMENT_1,
  appliedSegment2: SEGMENT_NONE,
  appliedSegment3: SEGMENT_NONE,
  advancedFilters: [] as AdvancedFilters,
  appliedAdvancedFilters: [] as AdvancedFilters,
  competitorSortMode: "Days/Assets" as DrilldownSortMode,
  competitorSortDirection: "desc" as DrilldownSortDirection,
};

/** My Brand-only fields — reset() must not touch these while in Competitors mode. */
const MY_BRAND_DEFAULTS = {
  myBrandObjectType: ALL_MY_BRAND_OBJECT_TYPES,
  myBrandEffectiveStatus: ALL_MY_BRAND_EFFECTIVE_STATUSES,
  appliedMyBrandObjectType: ALL_MY_BRAND_OBJECT_TYPES,
  appliedMyBrandEffectiveStatus: ALL_MY_BRAND_EFFECTIVE_STATUSES,
  myBrandStartDate: "",
  myBrandEndDate: "",
  appliedMyBrandStartDate: "",
  appliedMyBrandEndDate: "",
  myBrandSegment1: DEFAULT_SEGMENT_1,
  myBrandSegment2: SEGMENT_NONE,
  myBrandSegment3: SEGMENT_NONE,
  appliedMyBrandSegment1: DEFAULT_SEGMENT_1,
  appliedMyBrandSegment2: SEGMENT_NONE,
  appliedMyBrandSegment3: SEGMENT_NONE,
  myBrandAdvancedFilters: [] as AdvancedFilters,
  appliedMyBrandAdvancedFilters: [] as AdvancedFilters,
  myBrandKpis: DEFAULT_MY_BRAND_KPIS,
  myBrandHiddenColumns: [] as string[],
  myBrandSortMode: "Spend",
  myBrandSortDirection: "desc" as DrilldownSortDirection,
  audienceSegment: [] as string[],
  device: [] as string[],
  appliedAudienceSegment: [] as string[],
  appliedDevice: [] as string[],
  aspectRatio: [] as string[],
  appliedAspectRatio: [] as string[],
};

const INITIAL = { ...COMPETITORS_DEFAULTS, ...MY_BRAND_DEFAULTS };

function makeDefaultRule(field = DEFAULT_FILTER_FIELD): FilterRule {
  return { connector: "And", field, operator: "is", value: "" };
}

function makeDefaultGroup(connector: FilterConnector = "And", field = DEFAULT_FILTER_FIELD): FilterGroup {
  return { connector, rules: [makeDefaultRule(field)] };
}

/**
 * Drop (newField=null) or remap (newField=id) every advanced-filter rule referencing
 * `targetField`, then drop any group left with zero rules. Used when a My Brand KPI
 * pill is removed/replaced so `myBrandAdvancedFilters` never keeps a rule pointing at
 * a field id that's no longer a valid fieldOptions entry (AdvancedFiltersEditor has no
 * sensible fallback for an orphaned field — see removeMyBrandKpi/replaceMyBrandKpi).
 */
function reconcileAdvancedFilterField(
  groups: AdvancedFilters,
  targetField: string,
  newField: string | null,
): AdvancedFilters {
  return groups
    .map((g) => ({
      ...g,
      rules: newField
        ? g.rules.map((r) => (r.field === targetField ? { ...r, field: newField } : r))
        : g.rules.filter((r) => r.field !== targetField),
    }))
    .filter((g) => g.rules.length > 0);
}

export const useDrilldownFiltersStore = create<DrilldownFiltersState>()((set) => ({
  ...INITIAL,
  baseStartDate: "",
  baseEndDate: "",
  myBrandBaseStartDate: "",
  myBrandBaseEndDate: "",
  viewMode: "grid",
  setViewMode: (viewMode) => set({ viewMode }),
  setCompetitorSortMode: (competitorSortMode) => set({ competitorSortMode }),
  setCompetitorSortDirection: (competitorSortDirection) => set({ competitorSortDirection }),
  setMyBrandKpis: (myBrandKpis) => set({ myBrandKpis }),
  addMyBrandKpi: (id) =>
    set((state) =>
      state.myBrandKpis.includes(id) ? {} : { myBrandKpis: [...state.myBrandKpis, id] },
    ),
  removeMyBrandKpi: (id, visibleIds) =>
    set((state) => {
      const source = visibleIds ?? state.myBrandKpis;
      if (source.length <= MIN_MY_BRAND_KPIS) return {};
      const index = source.indexOf(id);
      if (index === -1) return {};
      const myBrandKpis = source.filter((kpiId) => kpiId !== id);
      // Ids that leave the selection are no longer valid fieldOptions entries.
      // When a visible list is passed, hidden/unknown ids leave too — drop their
      // rules rather than leave a dangling field for AdvancedFiltersEditor.
      const dropped = visibleIds
        ? state.myBrandKpis.filter((kpiId) => !myBrandKpis.includes(kpiId))
        : [id];
      const myBrandAdvancedFilters = dropped.reduce(
        (groups, droppedId) => reconcileAdvancedFilterField(groups, droppedId, null),
        state.myBrandAdvancedFilters,
      );
      const sortStillShown = myBrandKpis.includes(state.myBrandSortMode);
      if (visibleIds ? sortStillShown : state.myBrandSortMode !== id) {
        return { myBrandKpis, myBrandAdvancedFilters };
      }
      // The active sort's pill was removed — fall back to the next KPI in pill order
      // (the one now sitting at the removed slot, or the last one if the tail pill was
      // removed) so the query re-triggers against a KPI that's actually still shown,
      // rather than silently keep sorting by a metric with no visible column.
      const nextSortMode = myBrandKpis[index] ?? myBrandKpis[myBrandKpis.length - 1];
      return { myBrandKpis, myBrandSortMode: nextSortMode, myBrandAdvancedFilters };
    }),
  replaceMyBrandKpi: (oldId, newId) =>
    set((state) => {
      if (oldId === newId || state.myBrandKpis.includes(newId)) return {};
      const myBrandKpis = state.myBrandKpis.map((id) => (id === oldId ? newId : id));
      // Follow any advanced-filter rule against the replaced KPI over to the new one —
      // same reasoning as myBrandSortMode below, applied to fieldOptions' other consumer.
      const myBrandAdvancedFilters = reconcileAdvancedFilterField(state.myBrandAdvancedFilters, oldId, newId);
      // If the replaced pill was driving the active sort, follow it to the new KPI
      // (same direction) so swapping a pill re-triggers the query with the new sort —
      // otherwise the query would keep sorting by a KPI no longer shown as a pill.
      if (state.myBrandSortMode === oldId) {
        return { myBrandKpis, myBrandSortMode: newId, myBrandAdvancedFilters };
      }
      return { myBrandKpis, myBrandAdvancedFilters };
    }),
  toggleMyBrandColumn: (key) =>
    set((state) => ({
      myBrandHiddenColumns: state.myBrandHiddenColumns.includes(key)
        ? state.myBrandHiddenColumns.filter((hidden) => hidden !== key)
        : [...state.myBrandHiddenColumns, key],
    })),
  toggleMyBrandKpiSort: (id) =>
    set((state) => {
      if (state.myBrandSortMode !== id) {
        // Becoming the active sort also promotes the pill to the front of the
        // order — pill order drives both the toolbar's left-to-right layout and
        // the grid card's "big" KPI (index 0), so sorting by a KPI should make it
        // the leftmost/primary one, not just the active sort field.
        const myBrandKpis = [id, ...state.myBrandKpis.filter((kpiId) => kpiId !== id)];
        return { myBrandSortMode: id, myBrandSortDirection: "desc", myBrandKpis };
      }
      return { myBrandSortDirection: state.myBrandSortDirection === "desc" ? "asc" : "desc" };
    }),
  setObjectType: (objectType) => set({ objectType }),
  setEffectiveStatus: (effectiveStatus) => set({ effectiveStatus }),
  setMyBrandObjectType: (myBrandObjectType) => set({ myBrandObjectType }),
  setMyBrandEffectiveStatus: (myBrandEffectiveStatus) => set({ myBrandEffectiveStatus }),
  setAudienceSegment: (audienceSegment) => set({ audienceSegment }),
  setDevice: (device) => set({ device }),
  clearDevice: () =>
    set((state) => {
      if (state.device.length === 0 && state.appliedDevice.length === 0) return state;
      return { device: [], appliedDevice: [] };
    }),
  setAspectRatio: (aspectRatio) => set({ aspectRatio }),
  clearAspectRatio: () =>
    set((state) => {
      if (state.aspectRatio.length === 0 && state.appliedAspectRatio.length === 0) return state;
      return { aspectRatio: [], appliedAspectRatio: [] };
    }),
  // Dates are namespaced per mode (own remembered selection) — resolved via
  // isMyBrandMode() the same way setSegment resolves its active field below.
  setDateRange: (startDate, endDate) =>
    set(() =>
      isMyBrandMode()
        ? { myBrandStartDate: startDate, myBrandEndDate: endDate }
        : { startDate, endDate },
    ),
  // Segments are namespaced per mode (own remembered selection, same SEGMENT_OPTIONS
  // vocabulary) — resolved via isMyBrandMode() the same way the advanced-filter CRUD
  // actions below resolve their active field.
  setSegment: (slot, value) =>
    set(() => {
      const prefix = isMyBrandMode() ? "myBrandSegment" : "segment";
      // A secondary grouping under "No grouping" is meaningless — clear it with slot 1.
      if (slot === 1 && value === SEGMENT_NONE) {
        return {
          [`${prefix}1`]: SEGMENT_NONE,
          [`${prefix}2`]: SEGMENT_NONE,
          [`${prefix}3`]: SEGMENT_NONE,
        } as Partial<DrilldownFiltersState>;
      }
      return { [`${prefix}${slot}`]: value } as Partial<DrilldownFiltersState>;
    }),
  addSegment: (value) =>
    set((state) => {
      const prefix = isMyBrandMode() ? "myBrandSegment" : "segment";
      const segment1Key = `${prefix}1` as "segment1" | "myBrandSegment1";
      const segment2Key = `${prefix}2` as "segment2" | "myBrandSegment2";
      const segment3Key = `${prefix}3` as "segment3" | "myBrandSegment3";
      if (state[segment1Key] === SEGMENT_NONE) return { [segment1Key]: value } as Partial<DrilldownFiltersState>;
      if (state[segment2Key] === SEGMENT_NONE) return { [segment2Key]: value } as Partial<DrilldownFiltersState>;
      if (state[segment3Key] === SEGMENT_NONE) return { [segment3Key]: value } as Partial<DrilldownFiltersState>;
      return {}; // all three slots taken
    }),
  removeSegment: (slot) =>
    set(() => {
      const prefix = isMyBrandMode() ? "myBrandSegment" : "segment";
      return { [`${prefix}${slot}`]: SEGMENT_NONE } as Partial<DrilldownFiltersState>;
    }),
  // The six actions below all mutate whichever mode's draft advanced-filters list is
  // currently on screen — resolved via isMyBrandMode() rather than threaded as a
  // parameter, so callers (the toolbar) don't need to know or pass the active mode.
  addFilterGroup: (defaultField = DEFAULT_FILTER_FIELD) =>
    set((state) => {
      const key = isMyBrandMode() ? "myBrandAdvancedFilters" : "advancedFilters";
      return { [key]: [...state[key], makeDefaultGroup("And", defaultField)] } as Partial<DrilldownFiltersState>;
    }),
  removeFilterGroup: (groupIndex) =>
    set((state) => {
      const key = isMyBrandMode() ? "myBrandAdvancedFilters" : "advancedFilters";
      return { [key]: state[key].filter((_, i) => i !== groupIndex) } as Partial<DrilldownFiltersState>;
    }),
  setGroupConnector: (groupIndex, connector) =>
    set((state) => {
      const key = isMyBrandMode() ? "myBrandAdvancedFilters" : "advancedFilters";
      const groups = state[key].map((g, i) => (i === groupIndex ? { ...g, connector } : g));
      return { [key]: groups } as Partial<DrilldownFiltersState>;
    }),
  addFilterRule: (groupIndex, defaultField = DEFAULT_FILTER_FIELD) =>
    set((state) => {
      const key = isMyBrandMode() ? "myBrandAdvancedFilters" : "advancedFilters";
      const groups = state[key].map((g, i) =>
        i === groupIndex
          ? { ...g, rules: [...g.rules, makeDefaultRule(defaultField)] }
          : g,
      );
      return { [key]: groups } as Partial<DrilldownFiltersState>;
    }),
  removeFilterRule: (groupIndex, ruleIndex) =>
    set((state) => {
      const key = isMyBrandMode() ? "myBrandAdvancedFilters" : "advancedFilters";
      const group = state[key][groupIndex];
      if (!group) return {};
      const newRules = group.rules.filter((_, i) => i !== ruleIndex);
      if (newRules.length === 0) {
        return { [key]: state[key].filter((_, i) => i !== groupIndex) } as Partial<DrilldownFiltersState>;
      }
      const groups = state[key].map((g, i) => (i === groupIndex ? { ...g, rules: newRules } : g));
      return { [key]: groups } as Partial<DrilldownFiltersState>;
    }),
  updateFilterRule: (groupIndex, ruleIndex, patch) =>
    set((state) => {
      const key = isMyBrandMode() ? "myBrandAdvancedFilters" : "advancedFilters";
      const groups = state[key].map((g, i) => {
        if (i !== groupIndex) return g;
        const rules = g.rules.map((r, j) =>
          j === ruleIndex ? { ...r, ...patch } : r,
        );
        return { ...g, rules };
      });
      return { [key]: groups } as Partial<DrilldownFiltersState>;
    }),
  // Seeds BOTH modes' pairs unconditionally in one commit — unlike setDateRange, this
  // isn't "the active mode's edit," it's a mount/URL-change-time seed for both, each
  // from its own already-computed window (see the effect in DrilldownPage.tsx).
  initializeDateRange: (startDate, endDate, myBrandStartDate, myBrandEndDate) =>
    set({
      startDate,
      endDate,
      appliedStartDate: startDate,
      appliedEndDate: endDate,
      baseStartDate: startDate,
      baseEndDate: endDate,
      myBrandStartDate,
      myBrandEndDate,
      appliedMyBrandStartDate: myBrandStartDate,
      appliedMyBrandEndDate: myBrandEndDate,
      myBrandBaseStartDate: myBrandStartDate,
      myBrandBaseEndDate: myBrandEndDate,
    }),
  setBaseDateRange: (startDate, endDate) =>
    set(() =>
      isMyBrandMode()
        ? { myBrandBaseStartDate: startDate, myBrandBaseEndDate: endDate }
        : { baseStartDate: startDate, baseEndDate: endDate },
    ),
  applyFilters: () =>
    set((state) => {
      // Treat an empty selection as "all" — normalize to the full list so the
      // UI shows all checked and the query collapses back to 'All'.
      const objectType = state.objectType.length === 0 ? ALL_OBJECT_TYPES : state.objectType;
      const effectiveStatus = state.effectiveStatus.length === 0 ? ALL_EFFECTIVE_STATUSES : state.effectiveStatus;
      const myBrandObjectType =
        state.myBrandObjectType.length === 0 ? ALL_MY_BRAND_OBJECT_TYPES : state.myBrandObjectType;
      const myBrandEffectiveStatus =
        state.myBrandEffectiveStatus.length === 0 ? ALL_MY_BRAND_EFFECTIVE_STATUSES : state.myBrandEffectiveStatus;
      return {
        objectType,
        effectiveStatus,
        myBrandObjectType,
        myBrandEffectiveStatus,
        appliedObjectType: objectType,
        appliedEffectiveStatus: effectiveStatus,
        appliedMyBrandObjectType: myBrandObjectType,
        appliedMyBrandEffectiveStatus: myBrandEffectiveStatus,
        // Both modes' pairs commit independently — no mode check needed since they
        // never overlap (this is what fixes advanced filters/dates leaking across modes).
        appliedStartDate: state.startDate,
        appliedEndDate: state.endDate,
        appliedMyBrandStartDate: state.myBrandStartDate,
        appliedMyBrandEndDate: state.myBrandEndDate,
        appliedSegment1: state.segment1,
        appliedSegment2: state.segment2,
        appliedSegment3: state.segment3,
        appliedMyBrandSegment1: state.myBrandSegment1,
        appliedMyBrandSegment2: state.myBrandSegment2,
        appliedMyBrandSegment3: state.myBrandSegment3,
        appliedAdvancedFilters: state.advancedFilters,
        appliedMyBrandAdvancedFilters: state.myBrandAdvancedFilters,
        appliedAudienceSegment: state.audienceSegment,
        appliedDevice: state.device,
        appliedAspectRatio: state.aspectRatio,
      };
    }),
  discardFilters: () =>
    set((state) => ({
      objectType: state.appliedObjectType,
      effectiveStatus: state.appliedEffectiveStatus,
      myBrandObjectType: state.appliedMyBrandObjectType,
      myBrandEffectiveStatus: state.appliedMyBrandEffectiveStatus,
      audienceSegment: state.appliedAudienceSegment,
      device: state.appliedDevice,
      aspectRatio: state.appliedAspectRatio,
      startDate: state.appliedStartDate,
      endDate: state.appliedEndDate,
      myBrandStartDate: state.appliedMyBrandStartDate,
      myBrandEndDate: state.appliedMyBrandEndDate,
      segment1: state.appliedSegment1,
      segment2: state.appliedSegment2,
      segment3: state.appliedSegment3,
      myBrandSegment1: state.appliedMyBrandSegment1,
      myBrandSegment2: state.appliedMyBrandSegment2,
      myBrandSegment3: state.appliedMyBrandSegment3,
      advancedFilters: state.appliedAdvancedFilters,
      myBrandAdvancedFilters: state.appliedMyBrandAdvancedFilters,
    })),
  // Only the active mode's namespaced fields are reset — the inactive mode's filters
  // must survive, same as every other mutation in this store (see isMyBrandMode()).
  // Dates revert to that mode's own base (set by initializeDateRange), not to "" —
  // same reasoning as COMPETITORS_DEFAULTS/MY_BRAND_DEFAULTS zeroing them, then
  // immediately overriding with the live base value below.
  reset: () =>
    set((state) =>
      isMyBrandMode()
        ? {
            ...MY_BRAND_DEFAULTS,
            myBrandStartDate: state.myBrandBaseStartDate,
            myBrandEndDate: state.myBrandBaseEndDate,
            appliedMyBrandStartDate: state.myBrandBaseStartDate,
            appliedMyBrandEndDate: state.myBrandBaseEndDate,
          }
        : {
            ...COMPETITORS_DEFAULTS,
            startDate: state.baseStartDate,
            endDate: state.baseEndDate,
            appliedStartDate: state.baseStartDate,
            appliedEndDate: state.baseEndDate,
          },
    ),
  loadPresetPageState: (presetMode, page) => {
    if (presetMode === 'my-brand') {
      const myBrandPage = page as {
        myBrandObjectType: string[];
        myBrandEffectiveStatus: string[];
        audienceSegment: string[];
        device?: string[];
        aspectRatio?: string[];
        myBrandSegment1: string;
        myBrandSegment2: string;
        myBrandSegment3: string;
        myBrandAdvancedFilters: AdvancedFilters;
        myBrandKpis: string[];
        myBrandSortMode: string;
        myBrandSortDirection: DrilldownSortDirection;
        myBrandHiddenColumns?: string[];
        viewMode: DrilldownViewMode;
      };
      const myBrandAdvancedFilters = normalizeLegacyGroupbyRules(myBrandPage.myBrandAdvancedFilters, [
        myBrandPage.myBrandSegment1,
        myBrandPage.myBrandSegment2,
        myBrandPage.myBrandSegment3,
      ]);
      set((state) => ({
        myBrandObjectType: myBrandPage.myBrandObjectType,
        appliedMyBrandObjectType: myBrandPage.myBrandObjectType,
        myBrandEffectiveStatus: myBrandPage.myBrandEffectiveStatus,
        appliedMyBrandEffectiveStatus: myBrandPage.myBrandEffectiveStatus,
        audienceSegment: remapAudienceSegmentValues(myBrandPage.audienceSegment),
        appliedAudienceSegment: remapAudienceSegmentValues(myBrandPage.audienceSegment),
        device: myBrandPage.device ?? [],
        appliedDevice: myBrandPage.device ?? [],
        aspectRatio: myBrandPage.aspectRatio ?? [],
        appliedAspectRatio: myBrandPage.aspectRatio ?? [],
        myBrandSegment1: myBrandPage.myBrandSegment1,
        appliedMyBrandSegment1: myBrandPage.myBrandSegment1,
        myBrandSegment2: myBrandPage.myBrandSegment2,
        appliedMyBrandSegment2: myBrandPage.myBrandSegment2,
        myBrandSegment3: myBrandPage.myBrandSegment3,
        appliedMyBrandSegment3: myBrandPage.myBrandSegment3,
        myBrandAdvancedFilters,
        appliedMyBrandAdvancedFilters: hasCrossLevelOr(myBrandAdvancedFilters)
          ? state.appliedMyBrandAdvancedFilters
          : myBrandAdvancedFilters,
        myBrandKpis: myBrandPage.myBrandKpis,
        myBrandSortMode: myBrandPage.myBrandSortMode,
        myBrandSortDirection: myBrandPage.myBrandSortDirection,
        myBrandHiddenColumns: myBrandPage.myBrandHiddenColumns ?? [],
        viewMode: myBrandPage.viewMode,
      }));
      return;
    }
    const competitorsPage = page as {
      objectType: string[];
      effectiveStatus: string[];
      segment1: string;
      segment2: string;
      segment3: string;
      advancedFilters: AdvancedFilters;
      competitorSortMode: DrilldownSortMode;
      competitorSortDirection: DrilldownSortDirection;
      viewMode: DrilldownViewMode;
    };
    const advancedFilters = normalizeLegacyGroupbyRules(competitorsPage.advancedFilters, [
      competitorsPage.segment1,
      competitorsPage.segment2,
      competitorsPage.segment3,
    ]);
    set((state) => ({
      objectType: competitorsPage.objectType,
      appliedObjectType: competitorsPage.objectType,
      effectiveStatus: competitorsPage.effectiveStatus,
      appliedEffectiveStatus: competitorsPage.effectiveStatus,
      segment1: competitorsPage.segment1,
      appliedSegment1: competitorsPage.segment1,
      segment2: competitorsPage.segment2,
      appliedSegment2: competitorsPage.segment2,
      segment3: competitorsPage.segment3,
      appliedSegment3: competitorsPage.segment3,
      advancedFilters,
      appliedAdvancedFilters: hasCrossLevelOr(advancedFilters)
        ? state.appliedAdvancedFilters
        : advancedFilters,
      competitorSortMode: competitorsPage.competitorSortMode,
      competitorSortDirection: competitorsPage.competitorSortDirection,
      viewMode: competitorsPage.viewMode,
    }));
  },
  hydrateFromApplied: (payload) => {
    const objectType = payload.objectType.length > 0 ? payload.objectType : ALL_OBJECT_TYPES
    const effectiveStatus = payload.effectiveStatus.length > 0 ? payload.effectiveStatus : ALL_EFFECTIVE_STATUSES
    const myBrandObjectType =
      payload.myBrandObjectType.length > 0 ? payload.myBrandObjectType : ALL_MY_BRAND_OBJECT_TYPES
    const myBrandEffectiveStatus =
      payload.myBrandEffectiveStatus.length > 0 ? payload.myBrandEffectiveStatus : ALL_MY_BRAND_EFFECTIVE_STATUSES
    const advancedFilters = normalizeLegacyGroupbyRules(payload.advancedFilters, [
      payload.segment1,
      payload.segment2,
      payload.segment3,
    ])
    const myBrandAdvancedFilters = normalizeLegacyGroupbyRules(payload.myBrandAdvancedFilters, [
      payload.myBrandSegment1,
      payload.myBrandSegment2,
      payload.myBrandSegment3,
    ])
    set({
      objectType,
      effectiveStatus,
      myBrandObjectType,
      myBrandEffectiveStatus,
      startDate: payload.startDate,
      endDate: payload.endDate,
      myBrandStartDate: payload.myBrandStartDate,
      myBrandEndDate: payload.myBrandEndDate,
      segment1: payload.segment1,
      segment2: payload.segment2,
      segment3: payload.segment3,
      myBrandSegment1: payload.myBrandSegment1,
      myBrandSegment2: payload.myBrandSegment2,
      myBrandSegment3: payload.myBrandSegment3,
      viewMode: payload.viewMode,
      competitorSortMode: payload.competitorSortMode,
      competitorSortDirection: payload.competitorSortDirection,
      myBrandKpis: payload.myBrandKpis,
      myBrandHiddenColumns: payload.myBrandHiddenColumns,
      myBrandSortMode: payload.myBrandSortMode,
      myBrandSortDirection: payload.myBrandSortDirection,
      advancedFilters,
      myBrandAdvancedFilters,
      audienceSegment: remapAudienceSegmentValues(payload.audienceSegment),
      device: payload.device,
      aspectRatio: payload.aspectRatio,
      appliedObjectType: objectType,
      appliedEffectiveStatus: effectiveStatus,
      appliedMyBrandObjectType: myBrandObjectType,
      appliedMyBrandEffectiveStatus: myBrandEffectiveStatus,
      appliedStartDate: payload.startDate,
      appliedEndDate: payload.endDate,
      appliedMyBrandStartDate: payload.myBrandStartDate,
      appliedMyBrandEndDate: payload.myBrandEndDate,
      appliedSegment1: payload.segment1,
      appliedSegment2: payload.segment2,
      appliedSegment3: payload.segment3,
      appliedMyBrandSegment1: payload.myBrandSegment1,
      appliedMyBrandSegment2: payload.myBrandSegment2,
      appliedMyBrandSegment3: payload.myBrandSegment3,
      appliedAdvancedFilters: advancedFilters,
      appliedMyBrandAdvancedFilters: myBrandAdvancedFilters,
      appliedAudienceSegment: remapAudienceSegmentValues(payload.audienceSegment),
      appliedDevice: payload.device,
      appliedAspectRatio: payload.aspectRatio,
    })
  },
}));

function arraysEqualForDirty(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((v) => setB.has(v));
}

/** Whether draft page filters differ from applied for the active explore mode. */
export function isDrilldownDirty(
  state: DrilldownFiltersState,
  mode: "my-brand" | "competitors",
): boolean {
  const isMyBrand = mode === "my-brand";
  const objectTypeModified = isMyBrand
    ? !arraysEqualForDirty(state.myBrandObjectType, state.appliedMyBrandObjectType)
    : !arraysEqualForDirty(state.objectType, state.appliedObjectType);
  const effectiveStatusModified = isMyBrand
    ? !arraysEqualForDirty(state.myBrandEffectiveStatus, state.appliedMyBrandEffectiveStatus)
    : !arraysEqualForDirty(state.effectiveStatus, state.appliedEffectiveStatus);
  const startDate = isMyBrand ? state.myBrandStartDate : state.startDate;
  const endDate = isMyBrand ? state.myBrandEndDate : state.endDate;
  const appliedStartDate = isMyBrand ? state.appliedMyBrandStartDate : state.appliedStartDate;
  const appliedEndDate = isMyBrand ? state.appliedMyBrandEndDate : state.appliedEndDate;
  const dateModified = startDate !== appliedStartDate || endDate !== appliedEndDate;
  const segment1 = isMyBrand ? state.myBrandSegment1 : state.segment1;
  const segment2 = isMyBrand ? state.myBrandSegment2 : state.segment2;
  const segment3 = isMyBrand ? state.myBrandSegment3 : state.segment3;
  const appliedSegment1 = isMyBrand ? state.appliedMyBrandSegment1 : state.appliedSegment1;
  const appliedSegment2 = isMyBrand ? state.appliedMyBrandSegment2 : state.appliedSegment2;
  const appliedSegment3 = isMyBrand ? state.appliedMyBrandSegment3 : state.appliedSegment3;
  const advancedFilters = isMyBrand ? state.myBrandAdvancedFilters : state.advancedFilters;
  const appliedAdvancedFilters = isMyBrand
    ? state.appliedMyBrandAdvancedFilters
    : state.appliedAdvancedFilters;

  const audienceSegmentModified = isMyBrand
    ? !arraysEqualForDirty(state.audienceSegment, state.appliedAudienceSegment)
    : false;
  const deviceModified = isMyBrand
    ? !arraysEqualForDirty(state.device, state.appliedDevice)
    : false;
  const aspectRatioModified = isMyBrand
    ? !arraysEqualForDirty(state.aspectRatio, state.appliedAspectRatio)
    : false;

  return (
    objectTypeModified ||
    effectiveStatusModified ||
    dateModified ||
    audienceSegmentModified ||
    deviceModified ||
    aspectRatioModified ||
    segment1 !== appliedSegment1 ||
    segment2 !== appliedSegment2 ||
    segment3 !== appliedSegment3 ||
    JSON.stringify(advancedFilters) !== JSON.stringify(appliedAdvancedFilters)
  );
}
